import { __awaiter } from "tslib";
import "./textures.component.scss";
import { CONTAINERS_PATH, FOLDER_NAME_TEXTURES, FOLDER_NAME_THUMBS, FORMAT_TEXTURE, FORMAT_THUMB, TEXTURES_BASE_FOLDER, TTextureSizeEnum, TThumbnailPositionEnum, allContainerTypes, filterOnlyUsesSizes, getTextureName, getThumbnailName, } from "@tedivo/tedivo-textures-services-pure";
import { DivWithSpinner, IntegratedDialog, createSimpleAlert, } from "@tedivo/tedivo-ui";
import { TedivoForm, translateTedivoForm, } from "@tedivo/tedivo-form";
import { SmartTable, } from "@tedivo/tedivo-smart-table";
import { safeDateType, systemRights, } from "@tedivo/tvd-api-models";
import { assertNonNullish, formatDateInCurrentTimezone, pad3, } from "@tedivo/tedivo-pure-helpers";
import { AdminServices } from "../../../app/services";
import { EditDrawer } from "../../common/EditDrawer";
import { ListComponentWithActions } from "../../common/ListComponentWithActions";
import { abbreviateContainerType } from "../../../app/services/textures/abbreviateContainerType";
import { awaiter } from "@tedivo/tedivo-dom-helpers";
import createNotification from "../../../app/notifications/createNotification";
import { createTableUserStorageInstance } from "../../../app/preferences/createTableUserStorageInstance";
import { getTranslation } from "../../../app/i18/i18tn";
import goSquared from "../../../app/tracking/goSquared";
import { invertColor } from "./texturesRenderer/helpers/invertColor";
import { percentageColorClass } from "./texturesRenderer/helpers/percentageColorClass";
import { setAppTitle } from "../../../app/setAppTitle";
import { z } from "zod";
export class TVDAdminTexturesComponent extends ListComponentWithActions {
    constructor() {
        super();
        this.extraEditDrawer = new EditDrawer();
        this.h1Label = "textures:title";
        this.goSquaredLabel = "Admin/Textures";
        this.topLabel = "general:adminArea";
        this.createLabel = "textures:adminCreate";
        this.deleteLabel = "textures:adminDelete";
        this.addRefreshButton = true;
        const editServiceCall = (dta) => {
            const aliasesStr = (dta.aliases || [])
                .map((s) => s.trim())
                .filter(Boolean)
                .join(",");
            const usesSizes = (dta.usesSizes || [])
                .map((s) => s.trim())
                .filter(Boolean)
                .join(",");
            return AdminServices.textures.saveCarrierAndAliases({
                carrier: dta.carrier,
                aliases: aliasesStr,
                usesSizes,
            });
        };
        this.serviceListCall = AdminServices.textures.getConfigAll;
        this.serviceDeleteCall = AdminServices.textures.deleteCarrierAndAliases;
        this.serviceEditCall = editServiceCall;
        this.systemRightToEdit = systemRights.ORG.AppAdminTextures;
        const userOptionsStorage = createTableUserStorageInstance("adminTexturesList");
        this.tableConfig = (data) => createTexturesConfigTableConfig({
            carriersInfo: data,
            userOptionsStorage,
            onEditSaveFn: this.openEditModal,
            onDeleteFn: this.openDeleteModal,
            editDrawer: this.extraEditDrawer,
            actionBox: createActionBox(data),
            refreshDataCb: () => {
                this.needsUpdate = true;
                return Promise.resolve();
            },
        });
        this.createEditForm = createCarrierEditForm;
        setAppTitle(getTranslation("textures:title"));
        goSquared.trackPage("TEDIVO Admin Pages - Textures");
        function createActionBox(cachedData) {
            const showPrefixesUseBtn = document.createElement("sl-button");
            showPrefixesUseBtn.className = "tvd-button";
            showPrefixesUseBtn.innerHTML = getTranslation("textures:prefixesUse");
            showPrefixesUseBtn.variant = "primary";
            showPrefixesUseBtn.outline = true;
            showPrefixesUseBtn.style.order = "5";
            showPrefixesUseBtn.addEventListener("click", () => __awaiter(this, void 0, void 0, function* () {
                prefixesUsedTableInModal(cachedData);
            }));
            return showPrefixesUseBtn;
        }
    }
    connectedCallback() {
        const _super = Object.create(null, {
            connectedCallback: { get: () => super.connectedCallback }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.connectedCallback.call(this);
            this.extraEditDrawer.drawer.dataset.extra = "true";
            this.appendChild(this.extraEditDrawer.drawer);
        });
    }
}
TVDAdminTexturesComponent.observedAttributes = [];
customElements.define("tvd-admin-textures-component", TVDAdminTexturesComponent);
/**
 * Generates a table configuration object for displaying and managing texture carrier data in the admin interface.
 *
 * This function creates a configuration for a smart table component, allowing for sorting, filtering,
 * and performing actions (such as editing, deleting, and viewing) on texture carrier entries.
 * The table displays various fields including carrier name, aliases, configuration percentages,
 * missing configurations, texture percentages, missing textures, last update date, and available actions.
 *
 * @param params - The configuration parameters.
 * @param params.carriersInfo - Array of texture carrier data to be displayed in the table.
 * @param params.userOptionsStorage - Storage interface for user-specific table options and filters.
 * @param params.editDrawer - Drawer component used for editing carrier or texture data.
 * @param params.onEditSaveFn - Callback function invoked when a carrier is edited and saved.
 * @param params.onDeleteFn - Callback function invoked when a carrier is deleted.
 * @param params.refreshDataCb - Callback function to refresh the table data after changes.
 * @returns A table configuration object compatible with the smart table component for texture carriers.
 */
function createTexturesConfigTableConfig({ carriersInfo, userOptionsStorage, editDrawer, actionBox, onEditSaveFn, onDeleteFn, refreshDataCb, }) {
    const tableConfig = {
        className: "tvd-table tvd-table-smart-table",
        data: carriersInfo,
        defaultSortBy: "carrier",
        title: `${getTranslation("textures:carriers")}: ${carriersInfo.length}, ${getTranslation("textures:aliases")}: ${carriersInfo.reduce((acc, dta) => acc + dta.aliases.length, 0)}`,
        fields: [
            {
                name: "carrier",
                label: getTranslation("textures:carrier"),
                fixedWidth: "90px",
                mapper(v) {
                    var _a;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const color = ((_a = v.otherData) === null || _a === void 0 ? void 0 : _a.color) || "";
                    const dc = document.createDocumentFragment();
                    const sp = document.createElement("span");
                    sp.title = color;
                    sp.className = "tvd-carrier-color";
                    sp.style.setProperty("--tvd-carrier-color", color);
                    sp.style.setProperty("--tvd-carrier-color-shaded", invertColor(color, false));
                    const tt = document.createElement("span");
                    tt.innerHTML = v.carrier;
                    dc.appendChild(sp);
                    dc.appendChild(document.createTextNode(" "));
                    dc.appendChild(tt);
                    return dc;
                },
            },
            {
                name: "aliases",
                label: getTranslation("textures:aliases"),
                valueFunction: (dta) => {
                    if (dta.aliases && dta.aliases.length > 0) {
                        return dta.aliases.sort().join(", ");
                    }
                    else {
                        return "";
                    }
                },
            },
            {
                name: "configConts",
                label: getTranslation("textures:percentageConfig"),
                fixedWidth: "110px",
                type: "calculated",
                sortValueFunction: (dta) => {
                    return pad3(Math.round(calcPercentageConfig(dta)));
                },
                mapper: (dta) => {
                    const percentageConfig = calcPercentageConfig(dta);
                    const sp = document.createElement("div");
                    sp.className = `tvd-percentage-color ${percentageColorClass(percentageConfig)}`;
                    sp.innerHTML = `${Math.round(percentageConfig)}%`;
                    return sp;
                },
            },
            {
                name: "missingConfigs",
                notSortable: true,
                label: getTranslation("textures:missingConfig"),
                mapper: (dta) => calcMissingConfigs(dta).join(", "),
                fixedWidth: "200px",
                type: "calculated",
                sortValueFunction: () => "",
            },
            {
                name: "sizes",
                label: getTranslation("textures:percentageTextures"),
                fixedWidth: "110px",
                type: "calculated",
                sortValueFunction: (dta) => {
                    return pad3(Math.round(calcPercentageTextures(dta)));
                },
                mapper: (dta) => {
                    const percentageTextures = calcPercentageTextures(dta);
                    const sp = document.createElement("div");
                    sp.className = `tvd-percentage-color ${percentageColorClass(percentageTextures)}`;
                    sp.innerHTML = `${Math.round(percentageTextures)}%`;
                    return sp;
                },
            },
            {
                name: "missingTextures",
                label: getTranslation("textures:missingTextures"),
                notSortable: true,
                type: "calculated",
                mapper: (dta) => calcMissingTextures(dta).join(", "),
                sortValueFunction: () => "",
                fixedWidth: "180px",
            },
            {
                name: "lastUpd",
                label: getTranslation("general:common.modifiedAt"),
                valueFunction: (dta) => {
                    if (dta.modifiedAt) {
                        const d = safeDateType(dta.modifiedAt);
                        return formatDateInCurrentTimezone(d);
                    }
                    else {
                        return "";
                    }
                },
                sortValueFunction: (dta) => new Date(dta.modifiedAt || Date.now()).toISOString(),
                className: "centered",
                fixedWidth: "200px",
            },
            {
                name: "thumbs",
                label: getTranslation("general:common.view"),
                iconVariant: (dta) => Object.keys(dta.thumbs).length <
                    filterOnlyUsesSizes(dta.usesSizes, allContainerTypes).length
                    ? "warning"
                    : "primary",
                type: "icon",
                icon: "eye",
                notSortable: true,
                fixedWidth: "40px",
                smallPadding: true,
                onClick: (dta) => {
                    showViewModal(dta.carrier, Object.keys(dta.thumbs), filterOnlyUsesSizes(dta.usesSizes, allContainerTypes), Date.now());
                },
            },
            {
                name: "carrier",
                label: getTranslation("general:common.actions"),
                className: "files-actions",
                type: "actions",
                iconVariant: "primary",
                icon: "three-dots-vertical",
                notSortable: true,
                notFilterable: true,
                fixedWidth: "75px",
                smallPadding: true,
                onClick: (dta, name, table, value) => {
                    switch (value) {
                        case "editCarrier": {
                            onEditSaveFn === null || onEditSaveFn === void 0 ? void 0 : onEditSaveFn(dta.carrier, dta, carriersInfo);
                            break;
                        }
                        case "deleteCarrier":
                            onDeleteFn === null || onDeleteFn === void 0 ? void 0 : onDeleteFn(dta.carrier, dta, "carrier");
                            break;
                        case "editTextures": {
                            import(
                            /* webpackChunkName: "container3dEditor" */ "./texturesRenderer/Container3dEditor").then((module) => {
                                var _a;
                                const cEditor = new module.Container3dEditor(dta.carrier, dta.usesSizes || [], {
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    color: ((_a = dta.otherData) === null || _a === void 0 ? void 0 : _a.color) || "",
                                    containers: dta.configConts,
                                    lastUpd: safeDateType(dta.modifiedAt).getTime(),
                                }, (_, hasChanged) => {
                                    editDrawer.dataIsDirty = hasChanged;
                                });
                                const editD = editDrawer.getEditDrawer({
                                    title: getTranslation("textures:adminEditTextures", {
                                        carrier: dta.carrier,
                                    }),
                                    showUnits: false,
                                    dontScroll: true,
                                    extraButtons: [
                                        {
                                            label: "Re-do all containers thumbnails only",
                                            variant: "success",
                                            outline: true,
                                            value: "redoThumbs",
                                            onClick: () => __awaiter(this, void 0, void 0, function* () {
                                                editDrawer.disableButtons = true;
                                                const texturesToSave = yield cEditor.takeScreenshots(new Set(filterOnlyUsesSizes(dta.usesSizes, allContainerTypes)), new Set(filterOnlyUsesSizes(dta.usesSizes, allContainerTypes)));
                                                saveAllThumbs(dta, texturesToSave);
                                                editDrawer.disableButtons = false;
                                                return true;
                                            }),
                                        },
                                    ],
                                    onSave: () => __awaiter(this, void 0, void 0, function* () {
                                        editDrawer.disableButtons = true;
                                        const [, texturesToSave] = yield Promise.all([
                                            AdminServices.textures.saveCarrierConfig(cEditor.value),
                                            cEditor.takeScreenshots(undefined, new Set(filterOnlyUsesSizes(dta.usesSizes, allContainerTypes))),
                                        ]);
                                        if (texturesToSave.length === 0) {
                                            return true;
                                        }
                                        saveAllTextures(dta, texturesToSave, refreshDataCb);
                                        saveAllThumbs(dta, texturesToSave);
                                        editDrawer.disableButtons = false;
                                        return true;
                                    }),
                                    readOnlyMode: false,
                                });
                                editD.appendChild(cEditor.container);
                                editD.show();
                            });
                        }
                    }
                },
                options: (d) => {
                    const oOptions = [];
                    if (d.carrier !== "_")
                        oOptions.push({
                            label: getTranslation("textures:adminEdit"),
                            value: "editCarrier",
                            icon: "pencil",
                        });
                    oOptions.push({
                        label: getTranslation("textures:adminEditTextures", {
                            carrier: d.carrier,
                        }),
                        value: "editTextures",
                        icon: "image",
                    });
                    if (d.carrier !== "_")
                        oOptions.push({
                            label: getTranslation("general:common.delete"),
                            value: "deleteCarrier",
                            icon: "trash",
                            variant: "danger",
                        });
                    return oOptions;
                },
            },
        ],
        settings: {
            sortEnabled: true,
            sortVisible: true,
            userOptionsStorage,
            filters: {
                textFieldEnabled: true,
                textFieldFilterFields: ["carrier", "aliases"],
                initialTextFieldFilter: userOptionsStorage.retrieveFilterText(),
            },
            labelSortBy: getTranslation("general:common.sortBy"),
            labelSearch: getTranslation("general:common.filterBy"),
            actionBox,
        },
        pkFunction: (dta) => dta.carrier,
    };
    return tableConfig;
}
/**
 * Creates and returns a form for editing or creating a texture carrier entry.
 *
 * This form allows users to input a carrier code and a list of aliases, with validation
 * to ensure the carrier code is unique (case-insensitive) among existing carriers and
 * matches the required format. The form can be pre-filled with previous data for editing.
 *
 * @param submitButton - The SlButton instance to be used as the form's submit button.
 * @param prevData - (Optional) Previous carrier data to pre-fill the form for editing.
 * @param existingCarriers - (Optional) Array of existing carriers to check for uniqueness.
 * @returns An object containing:
 *   - `node`: The DOM node containing the form.
 *   - `tedivoForm`: The TedivoForm instance for further manipulation.
 *   - `submitFunction`: An async function to handle form submission and data saving.
 */
function createCarrierEditForm(submitButton, prevData, existingCarriers) {
    const holder = document.createElement("div");
    const formFields = [
        {
            name: "carrier",
            label: "textures:carrier",
            helpText: "textures:errors.carrierMinAndMax",
            type: "textBox",
            initialValue: (prevData === null || prevData === void 0 ? void 0 : prevData.carrier) || "",
            disabled: Boolean(prevData === null || prevData === void 0 ? void 0 : prevData.carrier),
        },
        {
            name: "aliases",
            label: "textures:aliases",
            type: "textArea",
            initialValue: (prevData === null || prevData === void 0 ? void 0 : prevData.aliases.join("\n")) || "",
            rows: 5,
        },
        {
            name: "usesSizes",
            label: "view:availableSizes",
            type: "checkboxesList",
            fieldset: true,
            initialValue: (prevData === null || prevData === void 0 ? void 0 : prevData.usesSizes) || ["20", "40", "45", "53"],
            options: ["20", "40", "45", "53"].map((l) => ({
                value: String(l),
                label: String(l),
            })),
        },
    ];
    const existingCarriersSet = new Set((existingCarriers === null || existingCarriers === void 0 ? void 0 : existingCarriers.map((c) => c.carrier.toUpperCase()).filter((s) => s !== (prevData === null || prevData === void 0 ? void 0 : prevData.carrier))) || []);
    const formValidator = z.object({
        carrier: z
            .string()
            .min(3)
            .max(5)
            .refine((val) => {
            const upperVal = val.toUpperCase();
            if (existingCarriersSet.has(upperVal)) {
                return false;
            }
            return /^[A-Z0-9]+$/.test(upperVal);
        }, {
            message: getTranslation("textures:errors.carrierAlreadyExists"),
        }),
        aliases: z.string().optional(),
        usesSizes: z.array(z.string()).refine((s) => s.length > 0),
    });
    const tedivoForm = new TedivoForm({
        fields: formFields,
        onSubmit: () => undefined,
        formValidator: formValidator,
        submitButton: submitButton,
        formProps: { autoFocusOnFirstInput: true },
        hiddenData: prevData,
    });
    translateTedivoForm({
        tedivoForm,
        getTranslation: getTranslation,
    });
    holder.appendChild(tedivoForm.form);
    return {
        node: holder,
        tedivoForm,
        submitFunction: submitPassedToEditDrawer,
    };
    function submitPassedToEditDrawer() {
        return __awaiter(this, void 0, void 0, function* () {
            const validResult = tedivoForm.doSubmitForm();
            if (validResult.success) {
                const { carrier, aliases: aliasesStr, usesSizes, } = tedivoForm.getValues();
                const aliases = (aliasesStr || "")
                    .split(/,\s*\n*/)
                    .map((f) => f.trim().toUpperCase().replace(/^\./, "").replace(/\.$/, ""))
                    .filter(Boolean);
                yield AdminServices.textures.saveCarrierAndAliases({
                    carrier,
                    aliases: aliases.join(","),
                    usesSizes: (usesSizes === null || usesSizes === void 0 ? void 0 : usesSizes.join(",")) || "",
                });
                return true;
            }
            else {
                return false;
            }
        });
    }
}
function saveAllTextures(dta, texturesToSave, cb) {
    const numOfTextures = texturesToSave.length;
    const labelOfTextures = texturesToSave
        .map((t) => t.txtr.containerType)
        .join(", ");
    const alrt = createNotification(`Creating container textures for ${dta.carrier} (${numOfTextures}): ${labelOfTextures}...`, "primary", "terminal", 30000);
    let curr = 0;
    Promise.all(texturesToSave.map((t) => {
        console.log(`Saving texture for ${t.txtr.carrier}-${t.txtr.containerType} (${Number(JSON.stringify(t, null, 0).length).toLocaleString()} bytes)`);
        AdminServices.textures.saveCarrierTexture(t.txtr).then((f) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (curr === 0)
                alrt.hide();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((_a = f.data) === null || _a === void 0 ? void 0 : _a.carrier) {
                yield awaiter(1000);
                curr++;
                if (curr === numOfTextures) {
                    createNotification(`Textures saved for ${dta.carrier} (${numOfTextures}): ${labelOfTextures}...`, "success", "check-circle", 3000);
                    if (cb) {
                        yield awaiter(3000);
                        cb === null || cb === void 0 ? void 0 : cb();
                    }
                }
            }
        }));
        return t.txtr.containerType;
    }));
}
function saveAllThumbs(dta, texturesToSave) {
    const numOfTextures = texturesToSave.length;
    const labelOfTextures = texturesToSave
        .map((t) => t.txtr.containerType)
        .join(", ");
    const alrt = createNotification(`Creating container thumbs for ${dta.carrier} (${numOfTextures}): ${labelOfTextures}...`, "primary", "terminal", 30000);
    let curr = 0;
    Promise.all(texturesToSave
        .filter((t) => t.thmbs)
        .map((t) => {
        console.log(`Saving texture for ${t.txtr.carrier}-${t.txtr.containerType} (${Number(JSON.stringify(t, null, 0).length).toLocaleString()} bytes)`);
        assertNonNullish(t.thmbs, "Thumbnails must be defined");
        AdminServices.textures.saveCarrierThumbs(t.thmbs).then((f) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (curr === 0)
                alrt.hide();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((_a = f.data) === null || _a === void 0 ? void 0 : _a.key) {
                yield awaiter(1000);
                curr++;
                if (curr === numOfTextures) {
                    createNotification(`Thumbs saved for ${dta.carrier} (${numOfTextures}): ${labelOfTextures}...`, "success", "check-circle", 3000);
                }
            }
        }));
        return t.txtr.containerType;
    }));
}
function calcPercentageConfig(dta) {
    if (dta.carrier === "_")
        return 100;
    const carrierContTypes = filterOnlyUsesSizes(dta.usesSizes, allContainerTypes);
    const totalCt = carrierContTypes.length;
    const configConts = (dta.configConts || []);
    const configs = dta.carrier === "_"
        ? totalCt
        : (configConts === null || configConts === void 0 ? void 0 : configConts.filter((v) => carrierContTypes.includes(v.type)).reduce((acc, v) => (v.logos.some((l) => l.url) ? acc + 1 : acc), 0)) || 0;
    return (configs / totalCt) * 100;
}
function calcPercentageTextures(dta) {
    const carrierContTypes = filterOnlyUsesSizes(dta.usesSizes, allContainerTypes);
    const totalCt = carrierContTypes.length;
    const containerTypes = dta.sizes || {};
    const configuredSizes = Object.keys(containerTypes);
    return ((filterOnlyUsesSizes(dta.usesSizes, configuredSizes).length / totalCt) * 100);
}
function calcMissingConfigs(dta) {
    if (dta.carrier === "_")
        return [];
    const carrierContTypes = filterOnlyUsesSizes(dta.usesSizes, allContainerTypes);
    const missingConfigs = new Set(carrierContTypes);
    const configConts = (dta.configConts || []);
    configConts === null || configConts === void 0 ? void 0 : configConts.forEach((v) => {
        if (v.logos.some((l) => l.url)) {
            missingConfigs.delete(v.type);
        }
    });
    return Array.from(missingConfigs).map(abbreviateContainerType).sort();
}
function calcMissingTextures(dta) {
    const carrierContTypes = filterOnlyUsesSizes(dta.usesSizes, allContainerTypes);
    const missingTerxtures = new Set(carrierContTypes);
    const sizes = dta.sizes;
    Object.keys(sizes).forEach((v) => {
        if (sizes[v] && sizes[v].length > 0) {
            missingTerxtures.delete(v);
        }
    });
    return Array.from(missingTerxtures).map(abbreviateContainerType).sort();
}
function showViewModal(carrier, confgContKeys, containers, lastUpd) {
    const div = document.createElement("div");
    const ul1 = document.createElement("ul");
    ul1.className = "tvd-textures-view-modal-list";
    const ul2 = document.createElement("ul");
    ul2.className = "tvd-textures-view-modal-list";
    const ul3 = document.createElement("ul");
    ul3.className = "tvd-textures-view-modal-list";
    const otherThumbs = [
        TThumbnailPositionEnum.Top,
        TThumbnailPositionEnum.Left,
        TThumbnailPositionEnum.Front,
        TThumbnailPositionEnum.Right,
        TThumbnailPositionEnum.Bottom,
        TThumbnailPositionEnum.Back,
    ];
    containers.forEach((containerType) => {
        {
            const li = document.createElement("li");
            const img = document.createElement("img");
            img.src = getThmName(carrier, containerType, TThumbnailPositionEnum.View3D, FORMAT_THUMB);
            img.alt = `${carrier} - ${containerType}`;
            img.title = `${carrier} - ${containerType}`;
            img.width = 480;
            img.height = 240;
            li.appendChild(img);
            ul1.appendChild(li);
            li.style.order = "10";
        }
        otherThumbs.forEach((thumbPosition) => {
            const li = document.createElement("li");
            li.className = "side";
            if (thumbPosition === TThumbnailPositionEnum.Front ||
                thumbPosition === TThumbnailPositionEnum.Back) {
                li.classList.add("front-back");
            }
            const img = document.createElement("img");
            img.src = getThmName(carrier, containerType, thumbPosition, FORMAT_THUMB);
            img.alt = `${carrier} - ${containerType} - ${thumbPosition}`;
            img.title = `${carrier} - ${containerType} - ${thumbPosition}`;
            img.width = 480;
            img.height = 240;
            li.appendChild(img);
            ul2.appendChild(li);
            li.style.order = "50";
        });
        {
            const li = document.createElement("li");
            const img = document.createElement("img");
            img.src = getTxtName(carrier, containerType, FORMAT_TEXTURE);
            img.alt = `3D Texture ${carrier} - ${containerType}`;
            img.title = `3D Texture ${carrier} - ${containerType}`;
            img.width = 240;
            img.height = 240;
            li.appendChild(img);
            ul3.appendChild(li);
            li.style.order = "100";
        }
    });
    div.appendChild(ul1);
    div.appendChild(ul2);
    div.appendChild(ul3);
    createSimpleAlert({
        title: `Thumbnails for ${carrier} (${confgContKeys.length})`,
        message: div,
        closeLabel: getTranslation("general:common.close"),
        width: "80%",
    });
    function getThmName(carrier, containerType, size, fmt) {
        return `${process.env.NX_PUBLIC_ASSETS_URL}/${TEXTURES_BASE_FOLDER}/${CONTAINERS_PATH}/${FOLDER_NAME_THUMBS}/${getThumbnailName(carrier, containerType, size, fmt)}?v=${lastUpd}`;
    }
    function getTxtName(carrier, containerType, fmt) {
        return `${process.env.NX_PUBLIC_ASSETS_URL}/${TEXTURES_BASE_FOLDER}/${CONTAINERS_PATH}/${FOLDER_NAME_TEXTURES}/${carrier}/${getTextureName(carrier, containerType, TTextureSizeEnum.S512, fmt)}?v=${lastUpd}`;
    }
}
function prefixesUsedTableInModal(carriersInfo) {
    return __awaiter(this, void 0, void 0, function* () {
        const modal = new IntegratedDialog({
            parentNode: document.body,
            buttonsAttrs: [
                {
                    label: getTranslation("general:common.close"),
                    variant: "primary",
                    value: "close",
                    type: "button",
                },
            ],
            removeDialogWhenHidden: true,
            width: "80%",
        });
        const divWithSpinner = new DivWithSpinner();
        divWithSpinner.setLoading(true);
        modal.show(getTranslation("textures:prefixesUse"), divWithSpinner);
        const prefixesUsed = yield AdminServices.textures.getPrefixesUse();
        const aliasesUsed = new Set();
        carriersInfo.forEach((dta) => {
            (dta.aliases || []).forEach((alias) => {
                aliasesUsed.add(alias.toUpperCase());
            });
        });
        const data = (prefixesUsed.data || []).filter((dta) => !aliasesUsed.has(dta.prefix.toUpperCase()) && dta.count > 0);
        const table = new SmartTable();
        table.initialize({
            className: "tvd-table tvd-table-smart-table",
            data,
            // title: getTranslation("textures:prefixesUse"),
            defaultSortBy: { name: "count", isNumeric: true, ascending: false },
            fields: [
                {
                    name: "prefix",
                    fixedWidth: "100px",
                    label: getTranslation("textures:prefixesUseTable.prefix"),
                    valueFunction: (dta) => dta.prefix,
                },
                {
                    name: "count",
                    type: "number",
                    fixedWidth: "100px",
                    className: "centered",
                    label: getTranslation("textures:prefixesUseTable.usedByCount"),
                },
                {
                    name: "counts",
                    label: getTranslation("textures:prefixesUseTable.usedBy"),
                    notSortable: true,
                    valueFunction: (dta) => JSON.stringify(dta.counts, null, 1)
                        .replace(/{/g, "")
                        .replace(/}/g, "")
                        .replace(/"/g, ""),
                },
            ],
            pkFunction: (dta) => dta.prefix,
            settings: {
                sortEnabled: true,
                sortVisible: true,
                filters: {
                    textFieldEnabled: true,
                    textFieldFilterFields: ["prefix"],
                },
                createSortIconFn: createSortIcon,
            },
        });
        divWithSpinner.appendChild(table);
        divWithSpinner.setLoading(false);
    });
}
function createSortIcon(asc) {
    const icon = document.createElement("sl-icon");
    icon.name = asc ? "sort-down-alt" : "sort-up-alt";
    return icon;
}
